import type {
  Deliverable,
  DeliverableStage,
  DeliverableStatus,
  DeliverableType,
} from '../types/portal'
import { DELIVERABLE_STAGES, DELIVERABLE_STATUSES, REVIEWABLE } from '../types/portal'

/** Client-facing heading for each stage, in the order the client sees them. */
export const CLIENT_STAGE_ORDER: DeliverableStage[] = DELIVERABLE_STAGES

export function stageHeading(stage: DeliverableStage): string {
  return stage === 'Review' ? 'In Review' : stage
}

/**
 * Statuses used before the workflow was consolidated, mapped forward. Stored
 * rows keep working and nothing has to be migrated in the database.
 */
const LEGACY_STATUS: Record<string, DeliverableStatus> = {
  'Not Started': 'Planned',
  'In Production': 'In Progress',
  Editing: 'In Progress',
  'Ready for Download': 'Approved',
}

/** Any stored or inherited status value, resolved to a current one. */
export function normaliseStatus(value: string): DeliverableStatus {
  const trimmed = (value ?? '').trim()
  if (DELIVERABLE_STATUSES.includes(trimmed as DeliverableStatus)) {
    return trimmed as DeliverableStatus
  }
  return LEGACY_STATUS[trimmed] ?? 'Planned'
}

/**
 * The production stage a status implies. Staff only ever set the status, so
 * this is the single source of truth for the stage — Portal and client both
 * read it, and they can never disagree.
 */
const STAGE_BY_STATUS: Record<DeliverableStatus, DeliverableStage> = {
  Planned: 'Pre-Production',
  'In Progress': 'Production',
  'Ready for Review': 'Review',
  'Changes Requested': 'Review',
  Approved: 'Final Delivery',
  Delivered: 'Final Delivery',
}

export function deliverableStage(status: DeliverableStatus): DeliverableStage {
  return STAGE_BY_STATUS[normaliseStatus(status)] ?? 'Pre-Production'
}

/** True once a version number is meaningful — Review onwards. */
export function isVersioned(status: DeliverableStatus): boolean {
  const stage = deliverableStage(status)
  return stage === 'Review' || stage === 'Final Delivery'
}

const TYPE_LABEL: Record<DeliverableType, string> = {
  Video: 'Video',
  Audio: 'Audio',
  Photography: 'Photography',
  Document: 'Document',
  Graphics: 'Graphics',
  Transcript: 'Transcript',
  Folder: 'Folder',
  Other: 'Item',
}

export function typeLabel(type: DeliverableType): string {
  return TYPE_LABEL[type] ?? 'Item'
}

/** "Video · V3" — the small line under a deliverable name. */
export function metaLabel(deliverable: Deliverable): string {
  return [typeLabel(deliverable.type), deliverable.version].filter(Boolean).join(' · ')
}

export function fileAction(deliverable: Deliverable): 'Download' | 'Open folder' {
  if (deliverable.type === 'Folder' || deliverable.type === 'Photography') return 'Open folder'
  if (deliverable.driveUrl.includes('/folders/')) return 'Open folder'
  return 'Download'
}

export function isReviewable(status: DeliverableStatus): boolean {
  return REVIEWABLE.includes(normaliseStatus(status))
}

export function clientStatusLabel(status: DeliverableStatus): string {
  switch (normaliseStatus(status)) {
    case 'Planned':
      return 'PLANNED'
    case 'In Progress':
      return 'IN PROGRESS'
    case 'Ready for Review':
      return 'READY FOR REVIEW'
    case 'Changes Requested':
      return 'CHANGES REQUESTED'
    case 'Approved':
      return 'APPROVED'
    case 'Delivered':
      return 'DELIVERED'
  }
}

/**
 * What the client actually reads for a status. Shown inside the status
 * selector so staff can see it while choosing, then it disappears — it is
 * never a permanent second line of interface.
 */
export function statusOptionLabel(status: DeliverableStatus): string {
  const client = clientStatusLabel(status)
  const readable = client.charAt(0) + client.slice(1).toLowerCase()
  return `${status}  ·  Client sees: ${readable}`
}

export function canDownload(status: DeliverableStatus): boolean {
  const current = normaliseStatus(status)
  return current === 'Approved' || current === 'Delivered'
}

/**
 * Which link fields a deliverable type can actually use, so a Photography or
 * Transcript item never shows a meaningless video or audio review field.
 */
export function linkFieldsFor(type: DeliverableType): {
  /** A separate audio review link, for audio deliverables only. */
  audio: boolean
  drive: boolean
  driveLabel: string
} {
  switch (type) {
    case 'Video':
      return { audio: false, drive: true, driveLabel: 'Video Review Link' }
    case 'Audio':
      return { audio: true, drive: false, driveLabel: '' }
    case 'Transcript':
      return { audio: false, drive: false, driveLabel: '' }
    case 'Photography':
      return { audio: false, drive: true, driveLabel: 'Gallery / Drive Folder' }
    case 'Folder':
      return { audio: false, drive: true, driveLabel: 'Drive Folder' }
    default:
      return { audio: false, drive: true, driveLabel: 'Drive File or Folder' }
  }
}

/** "Waiting 1 day" / "Waiting 6 days" — subtle urgency, not an alarm. */
export function waitingLabel(since: number | undefined): string {
  if (!since) return ''
  const days = Math.floor((Date.now() - since) / 86_400_000)
  if (days <= 0) return 'Waiting today'
  return `Waiting ${days} day${days === 1 ? '' : 's'}`
}
